/**
 * CSS for an image edge treatment, shared by the placed image on the
 * canvas (SpreadImage), the read-only preview, and the edge menu's
 * preview tiles — one source so the swatch the author picks looks exactly
 * like the treatment that lands on the page (and in the PDF, which bakes
 * the same SPREAD_IMAGE_EDGES numbers / edgeMaskSvg shapes).
 */
import type { CSSProperties } from 'react';
import type { SpreadImageEdge, SpreadImageShape } from '@historyjki/shared';
import { SPREAD_IMAGE_EDGES, edgeMaskDataUri, shapeMaskDataUri } from './spreadGeometry';

export function imageEdgeCss(
  edge: SpreadImageEdge,
  boxAspect: number,
  /** CSS length for the 'rounded' radius (cqw on the canvas, px in menus). */
  roundedRadius: string,
): CSSProperties {
  if (edge === 'rounded') {
    return { borderRadius: roundedRadius };
  }
  if (edge === 'feather') {
    const f = SPREAD_IMAGE_EDGES.feather.fadePct;
    const mask =
      `linear-gradient(to right, transparent, #000 ${f}%, #000 ${100 - f}%, transparent), ` +
      `linear-gradient(to bottom, transparent, #000 ${f}%, #000 ${100 - f}%, transparent)`;
    return {
      maskImage: mask,
      maskComposite: 'intersect',
      WebkitMaskImage: mask,
      WebkitMaskComposite: 'source-in',
    };
  }
  if (edge === 'vignette') {
    const v = SPREAD_IMAGE_EDGES.vignette;
    const mask =
      `radial-gradient(${v.radiusPct}% ${v.radiusPct}% at 50% 50%, ` +
      `#000 ${v.innerPct}%, transparent 100%)`;
    return { maskImage: mask, WebkitMaskImage: mask };
  }
  if (edge === 'torn' || edge === 'brush' || edge === 'scallop') {
    const mask = `url("${edgeMaskDataUri(edge, boxAspect)}")`;
    const common = {
      maskImage: mask,
      maskSize: '100% 100%',
      maskRepeat: 'no-repeat',
      WebkitMaskImage: mask,
      WebkitMaskSize: '100% 100%',
      WebkitMaskRepeat: 'no-repeat',
    } satisfies CSSProperties;
    return common;
  }
  return {};
}

/**
 * CSS mask for a cutout shape — same data-URI mechanism as the SVG-masked
 * edges, driven by the identical shapeEdgeMaskSvg the PDF exporter bakes,
 * so canvas, previews, picker tiles, and the printed page all agree. An
 * edge treatment stacked on the shape softens the cut silhouette itself
 * (feathered blob, torn wave, …) inside the same single mask.
 */
export function imageShapeCss(
  shape: SpreadImageShape,
  boxAspect: number,
  edge: SpreadImageEdge = 'sharp',
): CSSProperties {
  const mask = `url("${shapeMaskDataUri(shape, boxAspect, edge)}")`;
  return {
    maskImage: mask,
    maskSize: '100% 100%',
    maskRepeat: 'no-repeat',
    WebkitMaskImage: mask,
    WebkitMaskSize: '100% 100%',
    WebkitMaskRepeat: 'no-repeat',
  };
}
